import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { TableCell, TableRow, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { fixedTwoFractionDigitsCurrencyOptions } from 'constants/loanApplication';
import { formatNumberToCurrencyString, formatNumberToFixed3Decimals } from 'utils/number';

import { selectProgramAndPricingApplicationData } from 'store/loanApplications/selectors';

import RateTableCell from '../RateTableCell';

import { getFirstRateTableCellStyles, rateTableRowStyles } from './RateTableRow.styles';
import { getExtraTableRowOptions } from './RateTableRow.utils';

const RateTableRow = ({
  columnDefs,
  rowData,
  rowData: { noteRate, payment },
  onSelectLoanProgramOption,
  selectedLoanProgramOption,
  pendingChanges,
  programId,
  programDetails,
  isProgramExpired = false,
  isProgramIneligible = false,
}) => {
  const application = useSelector(selectProgramAndPricingApplicationData);

  const { hasOptionsForSelection, hasTempSelectedOption, hasNewLoanOptionId } =
    getExtraTableRowOptions({
      rowData,
      selectedLoanProgramOption,
      pendingChanges,
      application,
      programId,
      noteRate,
    });

  return (
    <TableRow sx={rateTableRowStyles}>
      <TableCell
        align="right"
        sx={getFirstRateTableCellStyles({
          hasOptionsForSelection,
          hasTempSelectedOption,
          hasNewLoanOptionId,
        })}
      >
        <p>{`${formatNumberToFixed3Decimals(noteRate)}%`}</p>

        {payment && (
          <Typography variant="medium14">
            {formatNumberToCurrencyString(payment, fixedTwoFractionDigitsCurrencyOptions)}
          </Typography>
        )}
      </TableCell>

      {columnDefs.map(({ id }) => (
        <RateTableCell
          cellData={rowData[`for${id}Days`]}
          isProgramExpired={isProgramExpired}
          isProgramIneligible={isProgramIneligible}
          key={id}
          lockTerm={id}
          noteRate={noteRate}
          pendingChanges={pendingChanges}
          programDetails={programDetails}
          programId={programId}
          selectedLoanProgramOption={selectedLoanProgramOption}
          onSelectLoanProgramOption={onSelectLoanProgramOption}
        />
      ))}
    </TableRow>
  );
};

RateTableRow.propTypes = {
  columnDefs: PropTypes.array.isRequired,
  rowData: PropTypes.object.isRequired,
  programId: PropTypes.string.isRequired,
  onSelectLoanProgramOption: PropTypes.func.isRequired,
  selectedLoanProgramOption: PropTypes.object,
  pendingChanges: PropTypes.object,
  programDetails: PropTypes.object,
  isProgramExpired: PropTypes.bool,
  isProgramIneligible: PropTypes.bool,
};

export default memo(RateTableRow);
